import { mdiAccount } from '@mdi/js';
import { Tooltip } from '@mollybet/ui';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { CurrencyCodes } from 'common/configs/currency';
import { Wallet } from 'common/state/banking/actions';
import { Bet } from 'common/state/bets/actions';
import { Customer, CustomerSummary } from 'common/state/customers/actions';
import React, { useMemo } from 'react';
import styled from 'styled-components';

export interface CustomerInfoTableProps {
  customer?: Customer;
  wallets?: Wallet[];
  summaries?: CustomerSummary[];
  bets?: Bet[];
  isLoading?: boolean;
  error?: React.ReactNode;
}

export interface InfoTableRow extends Customer {
  currencies: CurrencyCodes[];
}

export const CurrencyElement = styled.span<{ isDefault?: boolean }>`
  margin-right: 0.25rem;
  font-weight: ${({ isDefault }) => (isDefault ? 'bold' : undefined)};
`;

export const ActiveElement = styled.span`
  color: ${({ theme }) => theme.primary};
`;

export const InactiveElement = styled.span`
  color: ${({ theme }) => theme.danger};
`;

const YEAR_IN_MS = 1000 * 60 * 60 * 24 * 365;

export const CustomerInfoTable = ({
  customer,
  wallets,
  bets,
  summaries,
  isLoading,
  error,
}: CustomerInfoTableProps) => {
  const columns = useMemo(
    (): ColumnProp<InfoTableRow>[] => [
      {
        key: 'currencies',
        title: 'Currencies',
        valueFormatter: (row) =>
          row.currencies.map((currency) =>
            currency === customer?.default_currency ? (
              <Tooltip key={currency} content="Default Currency">
                <CurrencyElement isDefault>{currency}</CurrencyElement>
              </Tooltip>
            ) : (
              <CurrencyElement key={currency}>{currency}</CurrencyElement>
            )
          ),
      },
      { key: 'username', title: 'Username' },
      { key: 'first_name', title: 'First Name' },
      { key: 'last_name', title: 'Last Name' },
      { key: 'email', title: 'Email' },
      {
        key: 'jurisdiction',
        title: 'Jurisdication',
        valueFormatter: () =>
          !summaries ? (
            <span>Loading...</span>
          ) : (
            Array.from(
              summaries.reduce(
                (acc, summary) => acc.add(summary.customer_country),
                new Set<string>()
              )
            )
              .sort()
              .map((country) => (
                <CurrencyElement key={country}>{country}</CurrencyElement>
              ))
          ),
      },
      { key: 'risk', title: 'Risk', valueFormatter: () => '-' },
      { key: 'pep', title: 'PEP', valueFormatter: () => '-' },
      {
        key: 'active',
        title: 'Active',
        valueFormatter: () => (
          <Tooltip
            content={
              bets?.[0]
                ? `Last bet: ${new Date(bets[0].placed_at).toLocaleString()}`
                : 'No bets'
            }
          >
            {!bets ? (
              <span>Loading...</span>
            ) : bets[0] &&
              +new Date(bets[0].placed_at) > +new Date() - YEAR_IN_MS ? (
              <ActiveElement>Active</ActiveElement>
            ) : (
              <InactiveElement>Inactive</InactiveElement>
            )}
          </Tooltip>
        ),
      },
    ],
    [customer, bets, summaries]
  );

  const rows = useMemo(() => {
    if (!wallets || !customer) return null;

    const currencies: Set<CurrencyCodes> = customer?.default_currency
      ? new Set([customer.default_currency])
      : new Set();

    wallets?.forEach((wallet) => currencies.add(wallet.currency));

    return [{ ...customer, currencies: Array.from(currencies).sort() }];
  }, [customer, wallets]);

  return (
    <>
      <TableTitle
        icon={mdiAccount}
        title={`Customer: ${customer?.username || 'Loading...'}`}
      />
      <BaseTable
        columns={columns}
        rows={rows || []}
        csvName={customer?.username}
        isLoading={isLoading}
        error={error}
        isSortable={false}
        paginate={false}
      />
    </>
  );
};
